import React, { useState, useEffect } from 'react';
import { Button, Input } from '../../components/ui';
import Footer from '../../components/layout/Footer';
import { Edit, Save as SaveIcon, X, User as UserIcon, Mail, Phone, MapPin, Calendar, Loader2, Instagram, Facebook, Linkedin } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import PhotoUpload from '../../components/profile/PhotoUpload';
import { useSkills } from '../../hooks/useSkills';
import MyCampCard from '../../components/profile/MyCampCard';
import MyTasksList from '../../components/profile/MyTasksList';
import MyShiftsList from '../../components/profile/MyShiftsList';
import CityAutocomplete from '../../components/location/CityAutocomplete';
import { StructuredLocation } from '../../types';
import {
  PHONE_COUNTRY_CODE_OPTIONS,
  formatPhoneForDisplay,
  guessPhoneCountryCodeFromNumber
} from '../../utils/phone';

interface UserProfileData {
  firstName: string;
  lastName: string;
  email: string;
  phoneCountryCode: string;
  phoneNumber: string;
  location: StructuredLocation | null;
  yearsBurned: number;
  bio: string;
  playaName?: string;
  profilePhoto?: string;
  hasTicket: boolean | null;
  hasVehiclePass: boolean | null;
  interestedInEAP: boolean;
  interestedInStrike: boolean;
  arrivalDate?: string;
  departureDate?: string;
  skills?: string[];
  socialMedia?: {
    instagram?: string;
    facebook?: string;
    linkedin?: string;
  };
}

const formatLocationLabel = (location: StructuredLocation | null | undefined): string => {
  if (!location) return '';
  return [location.city, location.state, location.country].filter(Boolean).join(', ');
};

const UserProfile: React.FC = () => {
  const { user, updateUser } = useAuth();
  const { skills: availableSkills, loading: skillsLoading } = useSkills();
  const [profileData, setProfileData] = useState<UserProfileData>({
    firstName: '',
    lastName: '',
    email: '',
    phoneCountryCode: '+1',
    phoneNumber: '',
    location: null,
    yearsBurned: 0,
    bio: '',
    playaName: '',
    hasTicket: false,
    hasVehiclePass: false,
    interestedInEAP: false,
    interestedInStrike: false,
    skills: [],
    socialMedia: {}
  });
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [legacyCity, setLegacyCity] = useState('');

  // Helper function to convert Date to string
  const dateToString = (date: string | Date | undefined): string | undefined => {
    if (!date) return undefined;
    if (typeof date === 'string') return date;
    if (date instanceof Date) {
      return date.toISOString().split('T')[0];
    }
    return undefined;
  };

  useEffect(() => {
    if (user) {
      console.log('👤 [UserProfile] Loading user data:', user);
      console.log('👤 [UserProfile] Skills:', user.skills);
      console.log('👤 [UserProfile] Social Media:', user.socialMedia);
      console.log('👤 [UserProfile] Playa Name:', user.playaName);
      
      // Initialize profile data from user context
      setProfileData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        phoneCountryCode: user.phoneCountryCode || guessPhoneCountryCodeFromNumber(user.phoneNumber) || '+1',
        phoneNumber: user.phoneNumber || '',
        location: user.location?.city && user.location?.country && user.location?.countryCode &&
          user.location?.lat !== undefined && user.location?.lng !== undefined
          ? user.location as StructuredLocation
          : null,
        yearsBurned: user.yearsBurned || 0,
        bio: user.bio || '',
        playaName: user.playaName || '',
        profilePhoto: user.profilePhoto,
        hasTicket: user.hasTicket === true ? true : false,
        hasVehiclePass: user.hasVehiclePass === true ? true : false,
        interestedInEAP: user.interestedInEAP || false,
        interestedInStrike: user.interestedInStrike || false,
        arrivalDate: dateToString(user.arrivalDate),
        departureDate: dateToString(user.departureDate),
        skills: user.skills || [],
        socialMedia: user.socialMedia || {}
      });
      setLegacyCity(user.city || '');
    }
    setLoading(false);
  }, [user]);

  // Auto-enable edit mode for new users with minimal profile data
  useEffect(() => {
    if (!loading && user && profileData.firstName) {
      // Check if this is a newly created account (minimal profile data)
      const isNewUser = !profileData.bio && !profileData.playaName && profileData.yearsBurned === 0;
      if (isNewUser) {
        console.log('🔍 [UserProfile] New user detected, enabling edit mode');
        setIsEditing(true);
      }
    }
  }, [loading, profileData.firstName]);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');

      // Unify email display/edit: if changed, update login email first.
      const normalizedCurrentEmail = (user?.email || '').toLowerCase().trim();
      const normalizedProfileEmail = (profileData.email || '').toLowerCase().trim();
      if (normalizedProfileEmail && normalizedProfileEmail !== normalizedCurrentEmail) {
        const emailResponse = await api.changeEmail(normalizedProfileEmail);
        if (updateUser && emailResponse?.user) {
          updateUser(emailResponse.user);
        }
      }

      // Update remaining profile fields via API (exclude email, handled above)
      const { email, ...profilePayload } = profileData;
      console.log('🔄 [UserProfile] Saving profile data:', profilePayload);
      console.log('🔄 [UserProfile] Playa Name being saved:', profileData.playaName);
      const response = await api.put('/users/profile', profilePayload);
      console.log('✅ [UserProfile] Save response:', response);
      console.log('✅ [UserProfile] Response user object:', response.user);
      console.log('✅ [UserProfile] Response playaName:', response.user?.playaName);
      
      // Update the auth context with new data
      if (updateUser && response.user) {
        console.log('🔄 [UserProfile] Updating auth context with user:', response.user);
        updateUser(response.user);
      }
      
      setSuccess('Profile updated successfully!');
      setIsEditing(false);
    } catch (err: any) {
      console.error('Error saving profile:', err);
      setError(err.response?.data?.message || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    // Reset to original user data
    if (user) {
      setProfileData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        phoneCountryCode: user.phoneCountryCode || guessPhoneCountryCodeFromNumber(user.phoneNumber) || '+1',
        phoneNumber: user.phoneNumber || '',
        location: user.location?.city && user.location?.country && user.location?.countryCode &&
          user.location?.lat !== undefined && user.location?.lng !== undefined
          ? user.location as StructuredLocation
          : null,
        yearsBurned: user.yearsBurned || 0,
        bio: user.bio || '',
        playaName: user.playaName || '',
        profilePhoto: user.profilePhoto,
        hasTicket: user.hasTicket === true ? true : false,
        hasVehiclePass: user.hasVehiclePass === true ? true : false,
        interestedInEAP: user.interestedInEAP || false,
        interestedInStrike: user.interestedInStrike || false,
        arrivalDate: dateToString(user.arrivalDate),
        departureDate: dateToString(user.departureDate),
        skills: user.skills || [],
        socialMedia: user.socialMedia || {}
      });
      setLegacyCity(user.city || '');
    }
    setIsEditing(false);
    setError('');
    setSuccess('');
  };

  const handleInputChange = (field: keyof UserProfileData, value: any) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePasswordChange = async () => {
    try {
      setPasswordError('');
      setPasswordSuccess('');

      // Validation
      if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
        setPasswordError('All password fields are required');
        return;
      }

      if (passwordData.newPassword !== passwordData.confirmPassword) {
        setPasswordError('New passwords do not match');
        return;
      }

      if (passwordData.newPassword.length < 6) {
        setPasswordError('New password must be at least 6 characters');
        return;
      }

      // Call API to change password
      await api.put('/auth/change-password', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });

      setPasswordSuccess('Password changed successfully!');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (err: any) {
      console.error('Error changing password:', err);
      setPasswordError(err.response?.data?.message || 'Failed to change password');
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center min-h-[50vh]">
          <Loader2 className="w-8 h-8 animate-spin text-custom-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      {/* Consolidated Profile Section */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-h2 font-lato-bold text-custom-text">My Profile</h1>
          {!isEditing && (
            <Button
              variant="primary"
              onClick={() => setIsEditing(true)}
              className="flex items-center gap-2"
            >
              <Edit className="w-4 h-4" />
              Edit Profile
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div>
            {isEditing ? (
              <div className="space-y-5">
                <div className="max-w-[280px]">
                  <PhotoUpload
                    profilePhoto={profileData.profilePhoto}
                    onPhotoChange={(photoUrl) => handleInputChange('profilePhoto', photoUrl)}
                    isEditing={isEditing}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Input
                    value={profileData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    placeholder="First name"
                  />
                  <Input
                    value={profileData.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    placeholder="Last name"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-custom-text">Playa Name:</label>
                  <Input
                    value={profileData.playaName || ''}
                    onChange={(e) => handleInputChange('playaName', e.target.value)}
                    placeholder="Enter your playa name"
                    className="mt-1"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-custom-text flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    Location
                  </label>
                  <div className="mt-1">
                    <CityAutocomplete
                      value={profileData.location}
                      onChange={(location) => handleInputChange('location', location)}
                      label=""
                      placeholder="Search and select your city"
                      legacyValue={legacyCity}
                    />
                  </div>
                </div>

                <div className="max-w-[220px]">
                  <label className="text-sm font-medium text-custom-text flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    Number of Burns
                  </label>
                  <Input
                    type="number"
                    min="0"
                    value={profileData.yearsBurned}
                    onChange={(e) => handleInputChange('yearsBurned', parseInt(e.target.value) || 0)}
                    placeholder="Years burned"
                    className="mt-1"
                  />
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-4">
                <div className="relative flex-shrink-0">
                  {profileData.profilePhoto ? (
                    <img
                      src={profileData.profilePhoto}
                      alt={`${profileData.firstName} ${profileData.lastName}`}
                      className="w-24 h-24 rounded-full shadow object-cover bg-white"
                    />
                  ) : (
                    <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center">
                      <UserIcon className="w-10 h-10 text-gray-400" />
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-gray-900">
                    {profileData.firstName} {profileData.lastName}
                  </h2>
                  <div className="mt-3 space-y-2 text-sm text-custom-text-secondary">
                    <div>
                      <span className="font-medium text-custom-text">Playa Name: </span>
                      <span>{profileData.playaName || 'Not set'}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      <span className="font-medium text-custom-text">Location:</span>
                      <span>{formatLocationLabel(profileData.location) || (legacyCity ? `${legacyCity} (unverified)` : 'Location not set')}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      <span className="font-medium text-custom-text">Number of Burns:</span>
                      <span>
                        {profileData.yearsBurned === 0
                          ? 'Virgin (First Burn)'
                          : `${profileData.yearsBurned} ${profileData.yearsBurned === 1 ? 'Burn' : 'Burns'}`}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Email</p>
                {isEditing ? (
                  <Input
                    type="email"
                    value={profileData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="Enter account email"
                    className="mt-1"
                  />
                ) : (
                  <p className="text-sm text-custom-text mt-1 flex items-center gap-1">
                    <Mail className="w-4 h-4 text-gray-400" />
                    {profileData.email}
                  </p>
                )}
              </div>

              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Phone</p>
                {isEditing ? (
                  <div className="grid grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] gap-2 mt-1">
                    <Input
                      value={profileData.phoneCountryCode}
                      onChange={(e) => handleInputChange('phoneCountryCode', e.target.value)}
                      placeholder="+1"
                      list="user-profile-phone-country-codes"
                    />
                    <Input
                      value={profileData.phoneNumber}
                      onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                      placeholder="Enter phone number"
                    />
                    <datalist id="user-profile-phone-country-codes">
                      {PHONE_COUNTRY_CODE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </datalist>
                  </div>
                ) : (
                  <p className="text-sm text-custom-text mt-1 flex items-center gap-1">
                    <Phone className="w-4 h-4 text-gray-400" />
                    {formatPhoneForDisplay(profileData.phoneNumber, profileData.phoneCountryCode) || 'Not provided'}
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Ticket</p>
                {isEditing ? (
                  <div className="flex gap-4 mt-1">
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="radio"
                        name="hasTicket"
                        checked={profileData.hasTicket === true}
                        onChange={() => handleInputChange('hasTicket', true)}
                      />
                      Yes
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="radio"
                        name="hasTicket"
                        checked={profileData.hasTicket === false}
                        onChange={() => handleInputChange('hasTicket', false)}
                      />
                      No
                    </label>
                  </div>
                ) : (
                  <p className={`text-sm mt-1 font-medium ${profileData.hasTicket ? 'text-green-600' : 'text-red-600'}`}>
                    {profileData.hasTicket ? 'Yes' : 'No'}
                  </p>
                )}
              </div>

              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Vehicle Pass</p>
                {isEditing ? (
                  <div className="flex gap-4 mt-1">
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="radio"
                        name="hasVehiclePass"
                        checked={profileData.hasVehiclePass === true}
                        onChange={() => handleInputChange('hasVehiclePass', true)}
                      />
                      Yes
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="radio"
                        name="hasVehiclePass"
                        checked={profileData.hasVehiclePass === false}
                        onChange={() => handleInputChange('hasVehiclePass', false)}
                      />
                      No
                    </label>
                  </div>
                ) : (
                  <p className={`text-sm mt-1 font-medium ${profileData.hasVehiclePass ? 'text-green-600' : 'text-red-600'}`}>
                    {profileData.hasVehiclePass ? 'Yes' : 'No'}
                  </p>
                )}
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Bio</p>
              {isEditing ? (
                <textarea
                  value={profileData.bio}
                  onChange={(e) => handleInputChange('bio', e.target.value)}
                  placeholder="Tell us about yourself..."
                  className="w-full mt-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent min-h-[110px]"
                />
              ) : (
                <p className="text-sm text-custom-text-secondary mt-1 whitespace-pre-wrap">
                  {profileData.bio || 'No bio provided yet.'}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Alert Messages */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-r-lg">
          <p className="text-sm text-red-800 font-medium">{error}</p>
        </div>
      )}

      {success && (
        <div className="mb-6 p-4 bg-green-50 border-l-4 border-green-500 rounded-r-lg">
          <p className="text-sm text-green-800 font-medium">{success}</p>
        </div>
      )}

      {/* Profile activity sections */}
      <div className="space-y-6 mb-6">
        <MyCampCard />
        <MyTasksList />
        <MyShiftsList />
      </div>

      {/* Travel Dates Section */}
      <div className="bg-white rounded-xl shadow-lg p-8 mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Calendar className="w-5 h-5 text-purple-600" />
          Travel Dates
        </h2>
        
        {isEditing ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-600 mb-2">
                Arrival Date
              </label>
              <Input
                type="date"
                value={profileData.arrivalDate ? profileData.arrivalDate.split('T')[0] : ''}
                onChange={(e) => handleInputChange('arrivalDate', e.target.value)}
                className="bg-white"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-600 mb-2">
                Departure Date
              </label>
              <Input
                type="date"
                value={profileData.departureDate ? profileData.departureDate.split('T')[0] : ''}
                onChange={(e) => handleInputChange('departureDate', e.target.value)}
                className="bg-white"
              />
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <span className="text-sm text-gray-600">Arriving: </span>
              <span className="font-medium text-gray-900">
                {profileData.arrivalDate ? new Date(profileData.arrivalDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Not set'}
              </span>
            </div>
            <div>
              <span className="text-sm text-gray-600">Departing: </span>
              <span className="font-medium text-gray-900">
                {profileData.departureDate ? new Date(profileData.departureDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Not set'}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Early Arrival & Late Departure Section */}
      <div className="bg-white rounded-xl shadow-lg p-8 mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Calendar className="w-5 h-5 text-indigo-600" />
          Early Arrival & Late Departure
        </h2>
        
        {isEditing ? (
          <div className="space-y-4">
            <div>
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={profileData.interestedInEAP}
                  onChange={(e) => handleInputChange('interestedInEAP', e.target.checked)}
                  className="w-5 h-5 text-indigo-600 rounded focus:ring-indigo-500"
                />
                <div>
                  <span className="text-gray-900 font-medium">Interested in Early Arrival Pass (EAP)</span>
                  <p className="text-sm text-gray-500">Arrive before the event officially opens</p>
                </div>
              </label>
            </div>
            <div>
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={profileData.interestedInStrike}
                  onChange={(e) => handleInputChange('interestedInStrike', e.target.checked)}
                  className="w-5 h-5 text-indigo-600 rounded focus:ring-indigo-500"
                />
                <div>
                  <span className="text-gray-900 font-medium">Interested in Late Departure (Strike Team)</span>
                  <p className="text-sm text-gray-500">Stay after the event to help with cleanup</p>
                </div>
              </label>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <div>
              <span className="text-sm text-gray-600">Early Arrival: </span>
              <span className={`font-medium ${profileData.interestedInEAP ? 'text-green-600' : 'text-gray-400'}`}>
                {profileData.interestedInEAP ? '✓ Interested' : 'Not interested'}
              </span>
            </div>
            <div>
              <span className="text-sm text-gray-600">Late Departure: </span>
              <span className={`font-medium ${profileData.interestedInStrike ? 'text-green-600' : 'text-gray-400'}`}>
                {profileData.interestedInStrike ? '✓ Interested' : 'Not interested'}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Skills Section */}
      <div className="bg-white rounded-xl shadow-lg p-8 mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <UserIcon className="w-5 h-5 text-green-600" />
          Skills & Interests
        </h2>
        
        {isEditing ? (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2 mb-4">
              {profileData.skills?.map((skill, index) => (
                <div key={index} className="flex items-center gap-2 bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">
                  <span>{skill}</span>
                  <button
                    type="button"
                    onClick={() => {
                      const newSkills = profileData.skills?.filter((_, i) => i !== index) || [];
                      handleInputChange('skills', newSkills);
                    }}
                    className="hover:bg-green-200 rounded-full p-0.5"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-600 mb-2">
                Select Skills
              </label>
              <select
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
                value=""
                onChange={(e) => {
                  const skill = e.target.value;
                  if (skill && !profileData.skills?.includes(skill)) {
                    handleInputChange('skills', [...(profileData.skills || []), skill].sort());
                  }
                  // Reset dropdown
                  e.target.value = '';
                }}
                disabled={skillsLoading}
              >
                <option value="">{skillsLoading ? 'Loading skills...' : 'Choose a skill to add...'}</option>
                {availableSkills.map((skill) => (
                  <option key={skill} value={skill}>{skill}</option>
                ))}
              </select>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {profileData.skills && profileData.skills.length > 0 ? (
              profileData.skills.map((skill, index) => (
                <span key={index} className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">
                  {skill}
                </span>
              ))
            ) : (
              <span className="text-gray-400 italic">No skills added yet. Click "Edit Profile" to add some!</span>
            )}
          </div>
        )}
      </div>

      {/* Social Media Section */}
      <div className="bg-white rounded-xl shadow-lg p-8 mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Instagram className="w-5 h-5 text-pink-600" />
          Social Media
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Instagram */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-2">
              <Instagram className="w-4 h-4 text-pink-600" />
              Instagram
            </label>
            {isEditing ? (
              <Input
                value={profileData.socialMedia?.instagram || ''}
                onChange={(e) => handleInputChange('socialMedia', {
                  ...profileData.socialMedia,
                  instagram: e.target.value
                })}
                placeholder="@username or full URL"
                className="bg-white"
              />
            ) : (
              <div className="text-sm text-gray-900">
                {profileData.socialMedia?.instagram ? (
                  <a
                    href={profileData.socialMedia.instagram.startsWith('http') 
                      ? profileData.socialMedia.instagram 
                      : `https://instagram.com/${profileData.socialMedia.instagram.replace('@', '')}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-pink-600 hover:text-pink-700 flex items-center gap-1"
                  >
                    <Instagram className="w-4 h-4" />
                    {profileData.socialMedia.instagram}
                  </a>
                ) : (
                  <span className="text-gray-400">Not provided</span>
                )}
              </div>
            )}
          </div>

          {/* Facebook */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-2">
              <Facebook className="w-4 h-4 text-blue-600" />
              Facebook
            </label>
            {isEditing ? (
              <Input
                value={profileData.socialMedia?.facebook || ''}
                onChange={(e) => handleInputChange('socialMedia', {
                  ...profileData.socialMedia,
                  facebook: e.target.value
                })}
                placeholder="@username or full URL"
                className="bg-white"
              />
            ) : (
              <div className="text-sm text-gray-900">
                {profileData.socialMedia?.facebook ? (
                  <a
                    href={profileData.socialMedia.facebook.startsWith('http') 
                      ? profileData.socialMedia.facebook 
                      : `https://facebook.com/${profileData.socialMedia.facebook.replace('@', '')}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-700 flex items-center gap-1"
                  >
                    <Facebook className="w-4 h-4" />
                    {profileData.socialMedia.facebook}
                  </a>
                ) : (
                  <span className="text-gray-400">Not provided</span>
                )}
              </div>
            )}
          </div>

          {/* LinkedIn */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-2">
              <Linkedin className="w-4 h-4 text-blue-700" />
              LinkedIn
            </label>
            {isEditing ? (
              <Input
                value={profileData.socialMedia?.linkedin || ''}
                onChange={(e) => handleInputChange('socialMedia', {
                  ...profileData.socialMedia,
                  linkedin: e.target.value
                })}
                placeholder="@username or full URL"
                className="bg-white"
              />
            ) : (
              <div className="text-sm text-gray-900">
                {profileData.socialMedia?.linkedin ? (
                  <a
                    href={profileData.socialMedia.linkedin.startsWith('http') 
                      ? profileData.socialMedia.linkedin 
                      : `https://linkedin.com/in/${profileData.socialMedia.linkedin.replace('@', '')}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-700 hover:text-blue-800 flex items-center gap-1"
                  >
                    <Linkedin className="w-4 h-4" />
                    {profileData.socialMedia.linkedin}
                  </a>
                ) : (
                  <span className="text-gray-400">Not provided</span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Save/Cancel Buttons for Profile Edit */}
      {isEditing && (
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="flex justify-end space-x-4">
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={saving}
              className="flex items-center gap-2 px-6"
            >
              <X className="w-4 h-4" />
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-6"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <SaveIcon className="w-4 h-4" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Change Password Section - Full Width at Bottom */}
      <div className="bg-white rounded-xl shadow-lg p-8">
        <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2 border-b border-gray-200 pb-4">
          <SaveIcon className="w-5 h-5 text-blue-600" />
          Change Password
        </h2>

        {passwordError && (
          <div className="mb-4 p-4 bg-red-50 border-l-4 border-red-500 rounded-r-lg">
            <p className="text-sm text-red-800 font-medium">{passwordError}</p>
          </div>
        )}

        {passwordSuccess && (
          <div className="mb-4 p-4 bg-green-50 border-l-4 border-green-500 rounded-r-lg">
            <p className="text-sm text-green-800 font-medium">{passwordSuccess}</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
              Current Password
            </label>
            <Input
              type="password"
              value={passwordData.currentPassword}
              onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
              placeholder="Enter current password"
              className="bg-gray-50"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
              New Password
            </label>
            <Input
              type="password"
              value={passwordData.newPassword}
              onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
              placeholder="Enter new password"
              className="bg-gray-50"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
              Confirm New Password
            </label>
            <Input
              type="password"
              value={passwordData.confirmPassword}
              onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
              placeholder="Confirm new password"
              className="bg-gray-50"
            />
          </div>
        </div>

        <div className="mt-6 flex justify-end pt-6 border-t border-gray-200">
          <Button
            variant="primary"
            onClick={handlePasswordChange}
            className="flex items-center gap-2 px-6"
          >
            <SaveIcon className="w-4 h-4" />
            Change Password
          </Button>
        </div>
      </div>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default UserProfile;
